import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC } from 'react';

import { MainTitleProps } from './components.interfaces';

const MainTitle: FC<MainTitleProps> = (props: MainTitleProps) => {
  const { title, subTitle, onBackButtonClickHandler, backButtonText, endAdornment } = props;
  const theme = useTheme();
  const isLowerThanLaptop = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <Box
      data-testid="main-title-container"
      sx={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 0,
        paddingBottom: { xs: 1, sm: 2 },
        justifyContent: 'space-between',
        height: { xs: 'auto', sm: 'var(--main-title-height)' },
        minHeight: { xs: '48px', sm: 'var(--main-title-height)' },
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 1, sm: 0 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {onBackButtonClickHandler ? (
          <>
            {
              backButtonText ? (
                <Button
                  onClick={onBackButtonClickHandler}
                  data-testid="main-title-back-button"
                  sx={{
                    mr: { xs: 0.5, sm: 1 },
                  }}
                  startIcon={<ChevronLeft />}
                >
                  {backButtonText}
                </Button>
              ) : (
                <IconButton
                  onClick={onBackButtonClickHandler}
                  data-testid="main-title-back-button"
                  sx={{
                    mr: { xs: 0.5, sm: 1 },
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    },
                  }}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
              )}
          </>
        ) : null}
        <Typography
          variant={isLowerThanLaptop ? 'h6' : 'h4'}
          component="div"
          data-testid="main-title-text"
          aria-label={
            isLowerThanLaptop && subTitle ? `${title}, ${subTitle}` : title
          }
          sx={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 0.5,
            rowGap: 0.25,
            fontSize: { lg: '2rem', xl: '2.25rem' },
            fontWeight: { xs: 500, lg: 400 },
            lineHeight: { xs: 1.4, sm: 1.334 },
            minWidth: 0,
          }}
        >
          <Box component="span">{title}</Box>
          {isLowerThanLaptop && subTitle ? (
            <>
              <Box
                component="span"
                aria-hidden
                sx={{ color: 'text.secondary', fontWeight: 400, height: '24px' }}
              >
                <ChevronRight />
              </Box>
              <Box
                component="span"
                data-testid="main-title-sub-text"
                sx={{ color: 'text.secondary', fontWeight: 400 }}
              >
                {subTitle}
              </Box>
            </>
          ) : null}
        </Typography>
      </Box>
      {endAdornment && (
        <Box sx={{ ml: { xs: 1, sm: 2 } }} data-testid="main-title-end-adornment">
          {endAdornment}
        </Box>
      )}
    </Box>
  );
};

export default MainTitle;
